import type { Glossary } from './glossary.schema'

const glossaryEN: Glossary = {
  algorithm: {
    id: 'algorithm',
    term: 'Decompression Algorithm',
    synonyms: ['decompression model'],
    category: 'algorithm',
    level: 'basic',
    short_definition: 'A mathematical model that calculates how inert gas loads and offgasses from tissues during a dive, indicating when and where to stop during ascent.',
    full_explanation: `A **decompression algorithm** is the core of every dive computer. It is a mathematical model that simulates how nitrogen (and helium, if used) enter and leave body tissues during and after a dive.

The goal is to calculate the **safest possible ascent**, preventing the formation of gas bubbles that can cause **Decompression Sickness (DCS)**.

There are two main families:

- **Dissolved gas models** (Bühlmann ZH-L16, USN, DCIEM): assume that bubbles do not form if tissue gas tension stays below tabulated thresholds.
- **Bubble models** (VPM-B, RGBM): directly simulate the nucleation and growth of bubbles.

The choice of algorithm and its parameters influences the depth and duration of decompression stops, but **no algorithm is infallible**. Safety also depends on hydration, fitness, temperature and recent repetitive dives.`,
    related_terms: ['buhlmann_zhl16', 'vpm', 'gradient_factor'],
    references: [
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  buhlmann_zhl16: {
    id: 'buhlmann_zhl16',
    term: 'Bühlmann ZH-L16',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'A family of 16-compartment decompression algorithms developed by Prof. A.A. Bühlmann (University of Zurich) from the 1960s to the 1990s.',
    full_explanation: `The **Bühlmann ZH-L16 model** theoretically divides the body into 16 tissue compartments, each with its own **half-time** ranging from 4 minutes (fastest compartment, similar to blood) to 635 minutes (slowest, similar to cartilage).

During a dive, the model calculates minute by minute how much nitrogen (and possibly helium) has loaded into each compartment. Each compartment has an **M-value** — the maximum gas tension it can tolerate at a given ambient pressure without causing symptomatic bubbles.

There are three variants:

- **ZH-L16A**: purely theoretical coefficients, never used in practice.
- **ZH-L16B**: for **printed decompression tables**.
- **ZH-L16C**: for **real-time dive computers**, slightly more conservative. This is the near-universal default.

Almost all modern computers (Shearwater, Suunto, Garmin) use ZH-L16C, often combined with **Gradient Factors** to customise conservatism.`,
    related_terms: ['gradient_factor', 'm_value', 'compartment', 'half_time'],
    references: [
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  gradient_factor: {
    id: 'gradient_factor',
    term: 'Gradient Factor',
    synonyms: ['GF'],
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'A fraction of the M-value that is tolerated, configurable to personalise the conservatism of decompression.',
    full_explanation: `**Gradient Factors (GF)** are a mechanism introduced by Erik Baker in the 2000s to make Bühlmann's algorithm more conservative in a controlled way.

Instead of allowing inert gas tension in tissues to reach 100% of the M-value (Bühlmann's theoretical limit), it is capped at a **percentage fraction**. Example: GF = 80% means tolerating up to 80% of the M-value.

The system uses **two values**:

- **GF Low**: applied to the first (deepest) decompression stop. Determines **where** stops begin.
- **GF High**: applied at the surface. Determines the **final conservatism**.

Between the two points, GF grows **linearly** as depth decreases.

**Example settings**:

- 90/95: aggressive, close to pure Bühlmann
- 45/85: moderate, Shearwater default
- 30/70: conservative, used in deep technical dives`,
    worked_example: 'A diver descends to 40m, spends 25 minutes, and uses GF 40/85. The computer calculates the first stop at approximately 12m (deeper than with GF 90/95) using GF Low of 40%. During ascent, GF increases linearly to 85% at the surface, extending the 3m stop compared to more aggressive settings.',
    related_terms: ['m_value', 'buhlmann_zhl16', 'gf_low', 'gf_high', 'gf99'],
    references: [
      { title: 'Clearing Up The Confusion About "Deep Stops"', citation: 'Erik Baker, 1998' },
      { title: 'Decompression — Decompression Sickness', citation: 'A.A. Bühlmann, 1995' },
    ],
  },

  gf_low: {
    id: 'gf_low',
    term: 'GF Low',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'The fraction of M-value applied at the first (deepest) decompression stop. Determines where stops begin.',
    full_explanation: `**GF Low** controls the **depth of the first decompression stop**.

- A **low** GF Low (e.g. 30) → first stop is deeper. Greater protection of fast tissues during early ascent, at the cost of more loading on slow tissues.
- A **high** GF Low (e.g. 85) → first stop is shallower or absent. More direct ascent, profiles similar to older tables.

The debate between **deep stops** and **shallow stops** is one of the most discussed topics in modern diving. A 2011 NEDU study suggested that excessively deep stops may **worsen slow tissue loading**, leading the technical community to shift toward higher GF Low values (e.g. 50–70) compared to the past (30–40).`,
    related_terms: ['gf_high', 'gradient_factor', 'deep_stop'],
  },

  gf_high: {
    id: 'gf_high',
    term: 'GF High',
    category: 'algorithm',
    level: 'intermediate',
    short_definition: 'The fraction of M-value applied at the surface. Determines the final safety margin.',
    full_explanation: `**GF High** controls **how close to the M-value you can be** when you surface.

- GF High = 100% → tolerate 100% of M-value (pure Bühlmann, aggressive)
- GF High = 70% → tolerate only 70%, leaving a 30% safety margin

A **low** GF High extends the shallow stops (3–6m) because more gas must be offgassed before surfacing. These shallow stops particularly protect the **slow tissues**.

Typical values:

- 95: aggressive (near-pure Bühlmann)
- 85: Shearwater default, moderate
- 75–80: conservative, recommended for technical diving
- 70: very conservative, recommended when dehydrated, cold, fatigued, or after multiple repetitive dives`,
    related_terms: ['gf_low', 'gradient_factor', 'm_value'],
  },

  gf99: {
    id: 'gf99',
    term: 'GF99',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'Surfacing equivalent GF: if you surfaced right now, what would be the effective GF of the most-loaded compartment?',
    full_explanation: `**GF99** is a real-time value representing **how close to the limit** you are if you surfaced immediately.

It is calculated as: \`(current_tension - ambient_pressure) / (M_value_at_surface - ambient_pressure) × 100\`

Examples:

- GF99 = 30%: well below the limit, wide margin
- GF99 = 85%: at the limit of your GF High if that is set to 85%
- GF99 = 100%: exactly at the full M-value; beyond this is a violation

During correct decompression, GF99 oscillates — rising during ascents between stops and falling during stops themselves. Visualising it over time shows the decompression stress of a dive.`,
    related_terms: ['gradient_factor', 'm_value', 'leading_compartment'],
  },

  m_value: {
    id: 'm_value',
    term: 'M-value',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'The maximum inert gas tension a compartment can tolerate at a given ambient pressure without causing symptomatic bubbles.',
    full_explanation: `The **M-value** ("Maximum value") is the cornerstone of the Bühlmann model. It is the **tolerance threshold** specific to each of the 16 compartments.

Formula:

\`M = a + (P_ambient / b)\`

Where \`a\` (in bar) and \`b\` (dimensionless) are tabulated coefficients specific to each compartment and each gas (N₂ and He have separate coefficients). **Fast** compartments have higher M-values (more tolerant); **slow** compartments have lower M-values (more sensitive).

In practice: if gas tension in a compartment exceeds its M-value, the algorithm predicts bubble formation and requires a stop. The point at which a compartment is exactly at its M-value is the **ceiling**.`,
    related_terms: ['compartment', 'ceiling', 'gradient_factor', 'buhlmann_zhl16'],
  },

  compartment: {
    id: 'compartment',
    term: 'Compartment',
    synonyms: ['theoretical tissue'],
    category: 'physiology',
    level: 'basic',
    short_definition: 'A mathematical model of a body tissue type, characterised by a specific rate of inert gas loading and offgassing.',
    full_explanation: `A **compartment** does not correspond to a real tissue — it is a **mathematical abstraction** representing a behaviour. Bühlmann uses 16, each with a different **half-time**:

- **Fast compartments (1–5)**: 4–27 minutes. Represent highly perfused tissues: blood, bone marrow, nervous system. Load and offgas quickly.
- **Mid compartments (6–10)**: 38–146 minutes. Tissues such as muscles and organs.
- **Slow compartments (11–16)**: 187–635 minutes. Cartilage, adipose tissue, joints. Saturate slowly but retain gas long after a dive.

In a given dive, **different compartments become critical at different times**: fast ones during descent and the first few minutes; slow ones during long and repetitive dives.`,
    related_terms: ['half_time', 'm_value', 'leading_compartment', 'buhlmann_zhl16'],
  },

  half_time: {
    id: 'half_time',
    term: 'Half-time',
    synonyms: ['τ', 'tau', 'tissue half-time'],
    category: 'physiology',
    level: 'intermediate',
    short_definition: 'The time required for a compartment to halve the difference between its current inert gas tension and the ambient tension.',
    full_explanation: `The **half-time** (τ) is the time constant governing the loading/offgassing of a compartment. Example: if the half-time is 8 minutes and the initial difference between ambient and tissue is 4 bar, after 8 min the difference is 2 bar, after 16 min it is 1 bar, after 24 min it is 0.5 bar, and so on.

After **6 half-times** (~94% saturated/desaturated) the compartment is considered in equilibrium.

Half-times are tabulated for:

- **Nitrogen (N₂)**: 4 to 635 minutes
- **Helium (He)**: 1.51 to 240 minutes (~2.65× faster than N₂, because He is a smaller molecule)

This difference is why helium-containing mixes require specific decompression profiles.`,
    related_terms: ['compartment', 'buhlmann_zhl16'],
  },

  leading_compartment: {
    id: 'leading_compartment',
    term: 'Leading compartment',
    synonyms: ['controlling compartment'],
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'The compartment that, at a given moment, has the deepest ceiling and therefore limits ascent.',
    full_explanation: `The **leading compartment** is the one imposing the decompression constraint at a given moment.

It changes during the dive:

- **At the start of ascent**: **fast** compartments (1–3) are leading. They are saturated and impose the first stop.
- **During decompression**: as fast compartments offgas, **mid** compartments (5–8) take over.
- **During the last shallow stops**: **slow** compartments (10–14) become leading if the dive was long.

Visualising which compartment is leading moment by moment gives insight into **where the real risk lies** in that specific dive: short deep profiles load fast tissues; long moderate profiles load slow tissues.`,
    related_terms: ['compartment', 'ceiling', 'gf99'],
  },

  ceiling: {
    id: 'ceiling',
    term: 'Ceiling',
    category: 'safety',
    level: 'basic',
    short_definition: 'The minimum depth to which you can safely ascend at a given moment. Ascending above the ceiling is a decompression violation.',
    full_explanation: `The **ceiling** is the minimum safe depth calculated by the algorithm in real time. It is the point at which the **leading compartment** reaches exactly its M-value (with any GF applied).

If you ascend above the ceiling, the algorithm predicts that gas tension in the leading tissue will exceed the threshold, forming bubbles.

On well-configured computers, the ceiling is shown in real time as the **target depth of the current stop**. Example: if the ceiling is 8.4m, you must remain at depth ≥ 9m (stops are in 3m steps, rounded up).

A **ceiling violation** logged by a computer is a serious event: it means you ascended above the computed limit, even for just a few seconds. All computers report it and most record it in the dive log.`,
    related_terms: ['leading_compartment', 'm_value', 'decompression_stop'],
  },

  ndl: {
    id: 'ndl',
    term: 'NDL',
    synonyms: ['No-Decompression Limit'],
    category: 'safety',
    level: 'basic',
    short_definition: 'The maximum time you can stay at a given depth without requiring mandatory decompression stops on ascent.',
    full_explanation: `The **NDL (No-Decompression Limit)** is the time remaining before you enter **mandatory decompression**. The deeper you go, the faster the NDL decreases.

Typical examples (Bühlmann ZH-L16C, GF 45/85, air):

- 18m: ~56 min
- 24m: ~30 min
- 30m: ~17 min
- 36m: ~10 min
- 40m: ~7 min

When the NDL reaches 0, the dive becomes a **deco dive** and you must complete mandatory stops before surfacing.

**Recreational dives** are by definition conducted within the NDL, with a safety margin (a 3-minute safety stop at 5m is recommended even if not mandatory).`,
    related_terms: ['decompression_stop', 'safety_stop', 'ceiling'],
  },

  tts: {
    id: 'tts',
    term: 'TTS',
    synonyms: ['Time-to-Surface'],
    category: 'safety',
    level: 'intermediate',
    short_definition: 'Total time from current depth to the surface, including all mandatory decompression stops.',
    full_explanation: `**TTS (Time-to-Surface)** is a dynamic estimate of the time required to surface safely from the moment you begin ascent.

It includes:

- Ascent time between stops (at configured rate, typically 9 m/min)
- Duration of all decompression stops
- Any safety stop

It is a key metric for **gas management**: if your TTS is 25 minutes and you have gas for 20, you have a problem. Technical computers show **GTR (Gas Time Remaining)** alongside TTS to give real-time safety margin.

TTS varies continuously during the dive: it increases while you're on the bottom (loading tissues), stabilises during stops, and decreases as you offgas.`,
    related_terms: ['decompression_stop', 'ceiling', 'ndl'],
  },

  decompression_stop: {
    id: 'decompression_stop',
    term: 'Decompression stop',
    category: 'safety',
    level: 'basic',
    short_definition: 'A mandatory pause at a fixed depth during ascent to allow tissues to safely offgas inert gas.',
    full_explanation: `A **decompression stop** (or "deco stop") is a **mandatory pause** required by the algorithm during ascent. It occurs at depths that are multiples of 3 metres (e.g. 21m, 18m, 15m, 12m, 9m, 6m, 3m).

During a stop:

- Tissues offgas into the bloodstream
- Blood offgasses into the lungs
- Lungs exhale the gas

The duration of each stop is calculated by the algorithm: you stay until the leading compartment tension drops enough to allow continued ascent.

Key distinction:

- **Mandatory stop (deco)**: required by the algorithm; skipping it violates decompression
- **Safety stop**: recommended (3 min at 5m) even on NDL dives as a safety margin`,
    related_terms: ['safety_stop', 'ceiling', 'tts', 'ndl'],
  },

  safety_stop: {
    id: 'safety_stop',
    term: 'Safety stop',
    category: 'safety',
    level: 'basic',
    short_definition: 'A recommended 3-minute pause at 5 metres at the end of every dive, even if not required by the algorithm.',
    full_explanation: `The **safety stop** is a **non-mandatory but strongly recommended** pause endorsed by all training agencies at the end of every dive.

**Standard**: 3 minutes at 5 metres (some prefer 5 minutes).

Function:

- Allows further offgassing from fast tissues
- Reduces the risk of subclinical DCS
- Stabilises pressure before surfacing

Unlike a decompression stop, there is **no algorithmic penalty** if it is skipped — but the risk of issues increases. All modern dive computers suggest it at the end of every dive.`,
    related_terms: ['decompression_stop', 'ndl'],
  },

  deep_stop: {
    id: 'deep_stop',
    term: 'Deep stop',
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'An intermediate stop at greater depth than the first stop required by classical algorithms, introduced to reduce fast-tissue loading.',
    full_explanation: `**Deep stops** are pauses made **at greater depths** than those prescribed by pure Bühlmann.

The concept, popularised by Richard Pyle in the 1990s and formalised in bubble models (VPM-B), was that stopping deep prevents initial bubble formation in fast tissues.

In Bühlmann + GF, the same effect is achieved by **lowering GF Low**: with GF Low 30, the first stop is much deeper than with GF Low 85.

**Controversy**: in 2011, the **NEDU** (Naval Experimental Diving Unit) published a study suggesting that excessive deep stops may **worsen slow-tissue loading**, increasing DCS risk. Since then the technical community has shifted toward higher GF Low values (50–70) balanced with lower GF High (70–80).`,
    related_terms: ['gf_low', 'vpm', 'decompression_stop'],
    references: [
      { title: 'NEDU Deep Stops Study', citation: 'Naval Experimental Diving Unit, 2011' },
    ],
  },

  cns_percent: {
    id: 'cns_percent',
    term: 'CNS%',
    synonyms: ['CNS percentage', 'central toxicity'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'A cumulative estimate of central nervous system oxygen toxicity risk from breathing oxygen at high partial pressure.',
    full_explanation: `**CNS% (Central Nervous System percentage)** is an estimate of the risk of **hyperoxic convulsions** from breathing oxygen at elevated partial pressure.

It is based on **NOAA limits**: for each ppO₂ level there is a tabulated maximum exposure time. Actual exposure consumes a percentage of that limit.

Example: at ppO₂ 1.4 bar the NOAA single-exposure limit is 150 minutes. If you breathe that mix for 30 min, you have accumulated 30/150 = 20% CNS.

Practical limits:

- 80%: approaching the safety limit
- 100%: significant risk, avoid
- >100%: high-risk zone

CNS% **decays** at the surface with a half-time of about 90 minutes, so repetitive dives with short surface intervals accumulate. An **underwater hyperoxic convulsion** is almost always fatal due to drowning.`,
    related_terms: ['ppo2', 'otu', 'mod'],
  },

  otu: {
    id: 'otu',
    term: 'OTU',
    synonyms: ['Oxygen Tolerance Unit'],
    category: 'gas',
    level: 'advanced',
    short_definition: 'A cumulative measure of pulmonary oxygen toxicity (long-term irritative effects on the lungs).',
    full_explanation: `**OTU (Oxygen Tolerance Units)** measure **pulmonary oxygen toxicity**, distinct from central toxicity (CNS%).

While CNS% concerns **acute convulsions**, OTUs concern **pulmonary inflammation** from prolonged high-O₂ exposure.

Formula (simplified): \`OTU = t × ((ppO₂ - 0.5) / 0.5)^0.83\` for ppO₂ > 0.5 bar.

Recommended limits:

- Single dive: 300 OTU
- 24h: 600 OTU
- Multi-day week: 2500 OTU total

OTUs are particularly relevant for commercial and technical divers with prolonged exposures, and for patients in hyperbaric therapy chambers. Recreational single dives rarely approach OTU limits.`,
    related_terms: ['ppo2', 'cns_percent'],
  },

  ppo2: {
    id: 'ppo2',
    term: 'PpO₂',
    synonyms: ['partial pressure of oxygen'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'The partial pressure of oxygen in the breathing mix at a given depth. Determines hyperoxic toxicity risk.',
    full_explanation: `**PpO₂** (partial pressure of O₂) is the product of the O₂ fraction in the mix and the absolute pressure:

\`PpO₂ = F_O₂ × P_absolute\`

Examples (salt water, P = 1 bar per 10m):

- Air (21%) at 30m: 0.21 × 4.0 = **0.84 bar**
- Nitrox 32 at 30m: 0.32 × 4.0 = **1.28 bar**
- Nitrox 32 at 40m: 0.32 × 5.0 = **1.60 bar** ⚠️
- Pure O₂ at 6m: 1.00 × 1.6 = **1.60 bar**

Operational limits:

- **1.4 bar**: maximum recommended for the **bottom** phase
- **1.6 bar**: maximum recommended for the **deco** phase (at rest, lower CO₂)

Exceeding 1.6 bar during active diving is considered dangerous due to the risk of sudden hyperoxic convulsions.`,
    related_terms: ['mod', 'cns_percent', 'nitrox'],
  },

  mod: {
    id: 'mod',
    term: 'MOD',
    synonyms: ['Maximum Operating Depth'],
    category: 'gas',
    level: 'intermediate',
    short_definition: 'The maximum depth at which a given gas can be safely used, defined by the accepted ppO₂ limit.',
    full_explanation: `**MOD (Maximum Operating Depth)** is the maximum depth at which a gas is breathable while respecting a pre-defined ppO₂ limit.

Formula:

\`MOD (m) = ((ppO₂_limit / F_O₂) - 1) × 10\`

Examples (ppO₂ limit 1.4 bar):

- Air (21%): MOD ≈ 56m
- Nitrox 32: MOD ≈ 33m
- Nitrox 36: MOD ≈ 28m
- Nitrox 50: MOD ≈ 18m

For the **deco phase** ppO₂ 1.6 bar is often used, extending the MOD:

- Nitrox 50: deco MOD ≈ 22m
- Pure O₂: deco MOD ≈ 6m

Exceeding the MOD means entering acute hyperoxic risk territory.`,
    related_terms: ['ppo2', 'nitrox', 'cns_percent'],
  },

  surface_interval: {
    id: 'surface_interval',
    term: 'Surface interval',
    synonyms: ['SI'],
    category: 'physiology',
    level: 'basic',
    short_definition: 'Time spent at the surface between the end of one dive and the start of the next.',
    full_explanation: `The **surface interval** is the time between surfacing and the next dive. It is critical because it determines **how much residual gas** you have in your tissues when you start the next dive.

During the surface interval:

- **Fast compartments** (4–30 min HT) offgas quickly: after 1–2 hours they are nearly clean
- **Slow compartments** (>200 min HT) need many hours: some require **24–72 hours** for complete offgassing

A **short** surface interval (e.g. 30 min) leaves tissues significantly loaded: the computer accounts for this and proposes reduced NDLs for the next dive. A **long** SI (>6h) brings tissues close to a clean state but rarely fully zeroes them.`,
    related_terms: ['repetitive_dive', 'compartment', 'no_fly_time'],
  },

  repetitive_dive: {
    id: 'repetitive_dive',
    term: 'Repetitive dive',
    category: 'physiology',
    level: 'intermediate',
    short_definition: 'A dive performed while tissues still contain residual gas from a previous dive, typically within 12–24 hours.',
    full_explanation: `A dive is **repetitive** when it begins with tissues **not fully clean** from the previous one. The precise definition varies: PADI considers any dive within 6 hours repetitive; other standards extend this to 12 or 24 hours.

For a repetitive dive:

- The NDL is **reduced** compared to a first dive
- Any decompression stops are **longer**
- DCS risk is slightly elevated

Common-sense rules (empirically developed):

- Maximum depth of second dive < maximum depth of first dive
- Never more than 3–4 significant dives per day
- At least one full 24-hour rest during a multi-day trip
- Extended no-fly time (24h) after repetitive dives`,
    related_terms: ['surface_interval', 'ndl', 'no_fly_time'],
  },

  no_fly_time: {
    id: 'no_fly_time',
    term: 'No-fly time',
    category: 'safety',
    level: 'basic',
    short_definition: 'The waiting period after diving before boarding a flight, to avoid DCS from reduced cabin pressure.',
    full_explanation: `When flying, the aircraft cabin is **pressurised to roughly 2400–2500m altitude equivalent** (~0.75 bar instead of 1.013). This **pressure reduction** can extract residual gas from a diver's tissues, causing bubbles and DCS even after a dive that seemed safe.

**DAN (Divers Alert Network)** guidelines:

- **Single NDL dive**: wait at least **12 hours** before flying
- **Multiple dives or multi-day diving**: wait at least **18 hours**
- **Dives requiring decompression**: wait at least **24 hours**

Many operators use **24 hours as standard** for simplicity.

No-fly time also applies to ascending to high altitude (>300m altitude change) and low-altitude flights in unpressurised aircraft.`,
    related_terms: ['repetitive_dive', 'surface_interval', 'decompression_stop'],
    references: [
      { title: 'Flying After Diving Guidelines', citation: 'DAN (Divers Alert Network), 2002' },
    ],
  },

  vpm: {
    id: 'vpm',
    term: 'VPM',
    synonyms: ['Varying Permeability Model'],
    category: 'algorithm',
    level: 'advanced',
    short_definition: 'A family of bubble-based decompression algorithms, an alternative to dissolved-gas models such as Bühlmann.',
    full_explanation: `The **VPM (Varying Permeability Model)**, developed by Yount and Hoffman from the 1970s onwards, is a model that directly simulates **bubble formation and growth** in tissues, rather than simply assuming tension thresholds.

Key ideas:

- **Micro-nuclei** — pre-existing gaseous seeds of various radii in tissues
- Under pressure (descent) nuclei are compressed
- During ascent they can grow if external pressure drops enough
- The algorithm limits the **total volume of free gas** allowed

The modern **VPM-B** variant introduces an iterative mechanism ("Critical Volume Algorithm") for long profiles.

**Pros**: deeper and more gradual stops, more progressive load distribution.
**Cons**: less clinical validation than Bühlmann; ongoing debates about effectiveness (especially for slow tissues).

Many computers (Shearwater, Subsurface) support both Bühlmann GF and VPM-B.`,
    related_terms: ['buhlmann_zhl16', 'algorithm', 'deep_stop'],
    references: [
      { title: 'A Model for Predicting Diving Decompression Illness', citation: 'Yount & Hoffman, 1986' },
    ],
  },

  nitrox: {
    id: 'nitrox',
    term: 'Nitrox',
    synonyms: ['EAN', 'Enriched Air Nitrox'],
    category: 'gas',
    level: 'basic',
    short_definition: 'A breathing mix of nitrogen and oxygen with an O₂ percentage above the 21% found in air.',
    full_explanation: `**Nitrox** (also called **EAN** = Enriched Air Nitrox) is a mix of N₂ and O₂ in which oxygen is **enriched** compared to air.

Most common mixes:

- **EAN32** (Nitrox 32): 32% O₂, 68% N₂
- **EAN36** (Nitrox 36): 36% O₂, 64% N₂
- **EAN50** (deco gas): 50% O₂, 50% N₂

Advantages:

- Less nitrogen = less decompression loading at the same depth and time
- Extended NDLs compared to air (e.g. at 24m: air 30 min, EAN32 ~50 min)

Disadvantages:

- PpO₂ reaches the limit at shallower depth (see MOD)
- Cylinders must be certified "O₂ clean" if mix > 40% (fire risk in non-clean valves/regulators)
- Higher cost

For dives >30m, **air on the bottom + Nitrox for decompression** is common, optimising safety and decompression efficiency.`,
    related_terms: ['ppo2', 'mod', 'cns_percent', 'ndl'],
  },
}

export default glossaryEN
